/**
 * API Client
 *
 * Centralized API client for making requests to GCP backend.
 * Handles authentication, tenant injection, and error handling.
 */

import { getAuthClient } from './auth-client';
import { isLoggingOut, registerAuthResetHandlers, resetAuthSession } from './auth-reset';
import { NO_APP_ACCESS_CODE } from './no-app-access';
import { logger, redactUuids } from './logger';
import type {
  CaseVoiceCallsPage,
  CaseVoiceDetailResponse,
  CaseVoiceNameRequest,
  CaseVoicesResponse,
} from '@/types/voices';
import type {
  CaseResponse,
  CaseDetail,
  CreateCaseRequest,
  UpdateCaseRequest,
  DeleteCaseResponse,
  DeletionStatusResponse,
  DefendantIdentifierConfig,
  CaseFieldConfig,
  AdminField,
  CaseDetailsInput,
  FieldKind,
  FieldCreateInput,
  FieldUpdateInput,
  ResubmitFileResponse,
  RetryFailedForCaseResponse,
  BatchCreateRequest,
  BatchCreateResponse,
  BatchResponse,
  CaseTranscriptsResponse,
  CaseAiFlagsResponse,
  CaseOrientationResponse,
  CaseSummaryRetryResponse,
  InsightsCategoriesResponse,
  AiFlagReviewResponse,
  InsightsStateResponse,
  TranscriptDetail,
  TranscriptAudioResponse,
  TranscriptSummary,
  TranscriptNote,
  TranscriptNotesUpdateResponse,
  TranscriptSpeakersResponse,
  TimelineExportEstimate,
  TimelineExportFile,
  TimelineExportRequest,
  TranscriptStatusUpdateResponse,
  UpdateUtteranceResponse,
  UtteranceEdit,
  UserListResponse,
  PbxSettings,
  TenantListResponse,
  AddUserToTenantResponse,
  UserRole,
  InvitationListResponse,
  ResendInvitationResponse,
  CancelInvitationResponse,
  ErrorResponse,
  Conversation,
  ConversationListItem,
  ConversationMessage,
  ConversationExport,
  DeleteConversationResponse,
  UpdateConversationRequest,
  BatchSummarizationStatus,
  CaseParticipant,
  CaseParticipantsResponse,
  AddParticipantResponse,
  RemoveParticipantResponse,
  TransferOwnershipResponse,
  EligibleParticipantsResponse,
  CaseKpiResponse,
  CaseBatchResponse,
  BatchFileRecord,
  SubDocumentExtraction,
  PipelineTrace,
} from '@/types/gcp';
// API base URL is required at first use, NOT at module load.
//
// History: previously this was `const API_BASE_URL = ...` followed by an
// inline `if (!API_BASE_URL) throw` at module top level. That broke every
// build / test harness that imports this module transitively without
// dialing real endpoints: Storybook (axe-core a11y gate, bundle-size
// gate), Vitest, and any future Playwright surface. The CI workaround was
// a placeholder `NEXT_PUBLIC_API_URL=https://placeholder.invalid` env var
// on the affected jobs, which was hostile because it shifted the burden
// to every consumer instead of fixing the producer.
//
// The deferred check below preserves the prod-safety invariant ("never
// silently build URLs from undefined") while letting build-time tooling
// import this module freely. Cloud Build / Docker / runtime still fail
// loudly on the first real call if the substitution is missing.
function requireApiBaseUrl(): string {
  const url = process.env.NEXT_PUBLIC_API_URL;
  if (!url) {
    throw new Error(
      'NEXT_PUBLIC_API_URL environment variable is not set. ' +
      'This usually means the build was not configured correctly. ' +
      'Ensure Cloud Build substitutions include this variable.'
    );
  }
  return url;
}

interface TokenCache {
  token: string;
  expiry: number;
}

// ---------------------------------------------------------------------------
// Legacy-batch reshape helpers (uu-5-3 pre-deploy compatibility shim)
// ---------------------------------------------------------------------------
// These are only exercised by `getCaseBatchesLegacyFallback` when the
// consolidated `GET /api/cases/{id}/batches` endpoint returns 404 because
// the upload-api hasn't been redeployed since Story uu-5-3 landed.
// Remove this block when the fallback branch is retired.

const PIPELINE_STAGES = [
  'upload',
  'conversion',
  'transcription',
  'analysis',
  'embedding',
] as const;
type LegacyStage = (typeof PIPELINE_STAGES)[number];

interface LegacyStageEntry {
  status?: string;
  error?: string | null;
  file_name?: string;
}

interface LegacyFileStatus {
  file_id: string;
  overall_status?: string;
  upload?: LegacyStageEntry;
  conversion?: LegacyStageEntry;
  transcription?: LegacyStageEntry;
  analysis?: LegacyStageEntry;
  embedding?: LegacyStageEntry;
}

interface LegacyBatchStatusResponse {
  file_statuses?: LegacyFileStatus[];
}

function firstStageMatching(
  fs: LegacyFileStatus,
  predicate: (s: string | undefined) => boolean,
): LegacyStage | null {
  for (const stage of PIPELINE_STAGES) {
    if (predicate(fs[stage]?.status)) return stage;
  }
  return null;
}

function toBatchFileRecord(fs: LegacyFileStatus): BatchFileRecord {
  const overall = fs.overall_status ?? 'unknown';
  // Map legacy overall_status (completed/failed/processing/pending/...)
  // onto the `file_status` values the UI branches on ('complete'/'failed').
  let fileStatus: string = overall;
  if (overall === 'completed') fileStatus = 'complete';

  const failingStage = firstStageMatching(fs, (s) => s === 'failed');
  const currentStage =
    failingStage === null
      ? firstStageMatching(
          fs,
          (s) => s !== 'completed' && s !== 'failed' && s !== undefined,
        )
      : null;
  const failingReason =
    failingStage === 'conversion' ? (fs.conversion?.error ?? null) : null;

  return {
    file_id: fs.file_id,
    file_name: fs.upload?.file_name ?? fs.file_id,
    // Legacy endpoints do not expose file_size_bytes on the status
    // response. The File Uploads page does not render bytes, so 0 is
    // harmless here; if a future caller depends on this, switch the
    // fallback to also hit `/api/batch/{id}` for file metadata.
    file_size_bytes: 0,
    // `media_category` is also absent from the legacy status endpoint.
    // `<FileRow>` / `<StageBadge>` already gracefully handle `null`
    // (StageBadge falls back to audio-stage labels; type-specific
    // affordances like "View documents" stay hidden).
    media_category: null,
    file_status: fileStatus,
    created_date_time: null,
    current_stage: currentStage,
    failing_stage: failingStage,
    failing_reason: failingReason,
  };
}

function shapeLegacyBatch(
  batch: BatchResponse,
  statusBlob: unknown,
): CaseBatchResponse {
  const parsed = (statusBlob ?? {}) as LegacyBatchStatusResponse;
  const files = (parsed.file_statuses ?? []).map(toBatchFileRecord);
  return {
    batch_id: batch.batch_id,
    batch_name: batch.batch_name,
    file_count: batch.file_count,
    created_by: batch.created_by,
    created_date_time: batch.created_date_time ?? null,
    files,
  };
}

/**
 * Decode the OIDC standard expiration claim (exp) in seconds since epoch.
 * Returns the timestamp in milliseconds, or null on parse error.
 */
function decodeTokenExpiry(idToken: string | null | undefined): number | null {
  if (!idToken) return null;
  const parts = idToken.split('.');
  if (parts.length !== 3) return null;
  try {
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payloadB64.length % 4 === 0 ? '' : '='.repeat(4 - (payloadB64.length % 4));
    const json =
      typeof atob === 'function'
        ? atob(payloadB64 + pad)
        : typeof Buffer !== 'undefined'
          ? Buffer.from(payloadB64 + pad, 'base64').toString('utf-8')
          : '';
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Thrown by `reanalyzeCase` on a 409: a whole-case Re-run is already in
 * progress (claim held or an ingest scan in flight). Callers treat this as
 * "running", not a hard failure to retry.
 */
export class ReanalyzeConflictError extends Error {
  constructor() {
    super('A re-run is already in progress for this case.');
    this.name = 'ReanalyzeConflictError';
  }
}

/** The button was based on stale state and the server found no current work. */
export class ReanalyzeNoUpdateNeededError extends Error {
  readonly code = 'no_update_needed';

  constructor(message = 'No update is currently available.') {
    super(message);
    this.name = 'ReanalyzeNoUpdateNeededError';
  }
}

export interface ReanalyzeReceipt {
  status: 'enqueued';
  transcripts: number;
  update_id?: string;
  dispatched?: number;
  queued?: number;
  claim_lost?: number;
}

class ApiClient {
  private tokenCache: TokenCache | null = null;
  private tokenPromise: Promise<string | null> | null = null;

  private async getToken(): Promise<string | null> {
    const callTime = Date.now();
    logger.debug('[ApiClient.getToken] Method called at', new Date(callTime).toISOString());

    if (this.tokenCache && this.tokenCache.expiry > Date.now()) {
      const timeRemaining = Math.floor((this.tokenCache.expiry - Date.now()) / 1000);
      logger.debug('[ApiClient.getToken] Cache HIT, expires in', timeRemaining, 'seconds');
      return this.tokenCache.token;
    }

    if (this.tokenPromise) {
      logger.debug('[ApiClient.getToken] Token fetch already in progress, waiting');
      return this.tokenPromise;
    }

    this.tokenPromise = (async () => {
      try {
        const client = getAuthClient();
        logger.debug('[ApiClient.getToken] Fetching token via', client.providerName);

        const token = await client.getIdToken();
        if (!token) {
          logger.error('[ApiClient.getToken] No token available from auth client');
          return null;
        }

        const tokenFetchDuration = Date.now() - callTime;
        logger.debug('[ApiClient.getToken] Token fetched in', tokenFetchDuration, 'ms');

        const tokenExpiry = decodeTokenExpiry(token);
        const safeExpiry = tokenExpiry
          ? tokenExpiry - 5 * 60 * 1000
          : Date.now() + 55 * 60 * 1000;

        // Ensure we don't cache an already-expired/near-expired token indefinitely,
        // but do cache it for at least 10s to prevent spamming getIdToken in the same render tick.
        const cacheExpiry = Math.max(Date.now() + 10 * 1000, safeExpiry);

        this.tokenCache = {
          token,
          expiry: cacheExpiry,
        };

        return token;
      } catch (error) {
        logger.error('[ApiClient.getToken] Token fetch failed:', error);
        return null;
      } finally {
        this.tokenPromise = null;
      }
    })();

    return this.tokenPromise;
  }

  private async getHeaders(): Promise<HeadersInit> {
    logger.debug('[ApiClient.getHeaders] Building request headers');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = await this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      logger.debug('[ApiClient.getHeaders] Authorization header SET (token present)');
    } else {
      logger.error('[ApiClient.getHeaders] CRITICAL: No token available - Authorization header NOT SET');
      logger.error('[ApiClient.getHeaders] Request will fail with 401 if backend requires auth');
    }

    logger.debug('[ApiClient.getHeaders] Headers prepared:', Object.keys(headers));
    return headers;
  }

  /**
   * Get a valid Bearer token for use outside the standard request() flow
   * (e.g. Uppy XHR uploads, upload-utils).
   */
  public async getAuthToken(): Promise<string | null> {
    return this.getToken();
  }

  /**
   * Clear the token cache.
   * Must be called after password change since the provider may revoke old tokens.
   */
  public clearTokenCache() {
    this.tokenCache = null;
    this.tokenPromise = null;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  private async handleError(response: Response): Promise<never> {
    logger.error('[ApiClient.handleError] Request failed:', {
      status: response.status,
      statusText: response.statusText,
      url: redactUuids(response.url),
    });

    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    // Propagate the structured error envelope { code, message } so callers
    // can switch on `code` instead of matching the message string.
    let errorCode: string | undefined;
    let errorDetail: unknown;

    try {
      const errorData: ErrorResponse = await response.json();
      if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        errorDetail = errorData.detail;
      }

      if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        if (errorData.detail.message) {
          errorMessage = errorData.detail.message;
        }
        if (typeof errorData.detail.code === 'string') {
          errorCode = errorData.detail.code;
        }
      }
      logger.debug('[ApiClient.handleError] Error details from backend:', errorData);
    } catch {
      // If parsing fails, use default error message
      logger.warn('[ApiClient.handleError] Could not parse error response body');
    }

    if (response.status === 401) {
      if (isLoggingOut()) {
        // Deliberate logout in progress: token was just blocklisted by
        // signOut() and '/logout' navigation is already scheduled. Do
        // not attempt refresh or reset, which would redirect to /login
        // and defeat the logout.
        logger.debug('[ApiClient.handleError] 401 during logout - skipping refresh/reset');
        this.clearTokenCache();
      } else if (errorCode === NO_APP_ACCESS_CODE) {
        // The bearer verified but this account has no app access (deactivated
        // or revoked mid-session). A refreshed token gets the same answer, so
        // end the session instead of refreshing (lib/no-app-access.ts).
        logger.error('[ApiClient.handleError] 401 NO_APP_ACCESS - ending session');
        this.clearTokenCache();
        await resetAuthSession('no-app-access');
      } else {
        logger.error('[ApiClient.handleError] 401 UNAUTHORIZED - attempting token refresh');
        this.clearTokenCache();

        try {
          const client = getAuthClient();
          const refreshedToken = await client.getIdToken(true);
          if (!refreshedToken) {
            throw new Error('Token refresh returned null');
          }
          logger.debug('[ApiClient.handleError] Token refresh succeeded - caller should retry');
        } catch (refreshError) {
          logger.error('[ApiClient.handleError] Token refresh FAILED:', refreshError);
          await resetAuthSession('api-401-refresh-failed');
        }
      }
    }

    logger.error('[ApiClient.handleError] Throwing error:', redactUuids(errorMessage));
    const err = new Error(errorMessage) as Error & {
      status?: number;
      code?: string;
      detail?: unknown;
    };
    err.status = response.status;
    if (errorCode) {
      err.code = errorCode;
    }
    // The structured body, for callers that need more than the message: a
    // refused case-details save carries per-field `problems` here
    // (`CaseDetailsRefusal`), and a request-validation 422 carries FastAPI's
    // error array.
    if (errorDetail !== undefined) {
      err.detail = errorDetail;
    }
    throw err;
  }

  // ============================================================================
  // Cases
  // ============================================================================

  async getCases(tenantId: string): Promise<CaseResponse[]> {
    const headers = await this.getHeaders();

    // Add 20-second timeout for cold start handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const response = await fetch(
        `${requireApiBaseUrl()}/api/cases?tenant_id=${tenantId}`,
        {
          headers,
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      return this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      // Distinguish timeout from other errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - backend may be warming up. Please wait...');
      }
      throw error;
    }
  }

  async getCase(caseId: string, tenantId: string): Promise<CaseDetail> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * Fetch aggregate evidence KPI metrics for a case (Story uu-5-1).
   * Powers the 4-card KPI panel on the case overview page.
   */
  async getCaseKpi(caseId: string, tenantId: string): Promise<CaseKpiResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/kpi?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * Fetch the durable pipeline trace for a file (migration 172).
   * Env-gated server-side: returns 404 when ENABLE_PIPELINE_TRACE_DEBUG is
   * off. Callers should only invoke this when the `pipeline_trace_debug`
   * feature flag is enabled (the PipelineTracePanel self-hides otherwise).
   */
  async getFileTrace(fileId: string, tenantId: string): Promise<PipelineTrace> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/files/${fileId}/trace?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * Fetch all batches for a case with per-file pipeline details
   * (Story uu-5-3). Powers the File Uploads page batch list
   * (`<BatchList>`).
   *
   * Fast path (post-deploy):
   *   `GET /api/cases/{caseId}/batches`: a single round-trip that joins
   *   `file` against `pipeline_status` and `extraction_result`
   *   (including `sub_documents` for Epic 2 PDFs).
   *
   * Compatibility fallback (pre-deploy):
   *   If the dev upload-api hasn't been redeployed since uu-5-3 landed,
   *   the fast-path endpoint returns 404. In that case we transparently
   *   fall back to the pre-existing endpoints that shipped before this
   *   epic: `GET /api/batches?case_id=…` for batch rows and
   *   `GET /api/batch/{id}/status` per batch for per-file pipeline
   *   status. We reshape the result into `CaseBatchResponse[]` so the
   *   UI contract is preserved; `sub_documents` is omitted (Epic 2
   *   backend data isn't available on the legacy endpoints). Once the
   *   backend is redeployed the fast path resumes automatically with
   *   no code change.
   */
  async getCaseBatches(
    caseId: string,
    tenantId: string,
  ): Promise<CaseBatchResponse[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/batches?tenant_id=${tenantId}`,
      { headers }
    );

    if (response.status === 404) {
      logger.warn(
        '[ApiClient.getCaseBatches] /api/cases/{id}/batches not deployed; ' +
        'falling back to pre-epic endpoints',
      );
      return this.getCaseBatchesLegacyFallback(caseId, tenantId);
    }

    return this.handleResponse<CaseBatchResponse[]>(response);
  }

  /**
   * Aggregate `CaseBatchResponse[]` from the pre-uu-5-3 endpoints that
   * are already live on the deployed upload-api. Called only when the
   * consolidated endpoint returns 404.
   *
   * Round-trip cost: 1 + N (one batch list + one `/status` per batch).
   * Acceptable only as a transient compatibility shim: the whole
   * point of the uu-5-3 endpoint was to collapse this to a single
   * call. Remove this branch once the backend is redeployed and the
   * fast path has been observed working in all environments.
   */
  private async getCaseBatchesLegacyFallback(
    caseId: string,
    tenantId: string,
  ): Promise<CaseBatchResponse[]> {
    const batches = await this.getBatches({ caseId, tenantId });
    if (batches.length === 0) return [];

    const statusResults = await Promise.all(
      batches.map((b) =>
        this.getBatchStatus(b.batch_id, tenantId)
          .then((s) => ({ batchId: b.batch_id, status: s }))
          .catch((err) => {
            logger.warn(
              '[ApiClient.getCaseBatchesLegacyFallback] status fetch failed',
              { batchId: b.batch_id, err },
            );
            return { batchId: b.batch_id, status: null };
          }),
      ),
    );
    const statusByBatch = new Map<string, unknown>();
    for (const r of statusResults) statusByBatch.set(r.batchId, r.status);

    return batches.map((b) =>
      shapeLegacyBatch(b, statusByBatch.get(b.batch_id)),
    );
  }

  /**
   * Fetch metadata matching summary for a batch (Story 4.2 / 5.3).
   *
   * The deployed backend endpoint currently returns the raw
   * `metadata_files` array (plus stuck-metadata diagnostics).
   * `MetadataMatchSummaryResponse` is the aggregated shape the UI
   * consumes: `useMetadataMatchSummary` performs the aggregation
   * client-side. That hook also handles the 404 / missing-endpoint
   * case gracefully by rendering nothing. This method therefore
   * returns the raw envelope typed as `unknown`; the hook parses it.
   */
  async getMetadataMatchSummary(
    batchId: string,
    tenantId: string,
  ): Promise<unknown> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/batches/${batchId}/metadata-summary?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse<unknown>(response);
  }

  async createCase(data: CreateCaseRequest) {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/cases`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async updateCase(caseId: string, tenantId: string, data: UpdateCaseRequest) {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}?tenant_id=${tenantId}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse(response);
  }

  async deleteCase(caseId: string, tenantId: string): Promise<DeleteCaseResponse> {
    // Use local API route to avoid CORS issues with DELETE requests
    const response = await fetch(
      `/api/cases/${caseId}?tenant_id=${tenantId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return this.handleResponse(response);
  }

  /**
   * The real outcome of a deletion. `deleteCase` resolving means only that a
   * job row was written; the worker runs later and can fail (#1578).
   */
  async getDeletionStatus(
    caseId: string,
    tenantId: string
  ): Promise<DeletionStatusResponse> {
    const response = await fetch(
      `/api/cases/${caseId}/deletion-status?tenant_id=${tenantId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Defendant Identifier Config
  // ============================================================================

  async getDefendantIdentifierConfig(tenantId: string): Promise<DefendantIdentifierConfig[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/defendant-identifier-config?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async getCaseFieldConfig(tenantId: string): Promise<CaseFieldConfig[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/case-field-config?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Case details: the drawers' all-or-nothing save
  // ============================================================================

  /**
   * Create a case with its field values and defendants. A 422 carries
   * `detail: CaseDetailsRefusal` and means nothing was saved.
   */
  async createCaseDetails(tenantId: string, data: CaseDetailsInput): Promise<CaseDetail> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/case-details?tenant_id=${tenantId}`,
      { method: 'POST', headers, body: JSON.stringify(data) }
    );
    return this.handleResponse(response);
  }

  /** Save Edit Case Details. A 422 means nothing was saved. */
  async updateCaseDetails(
    caseId: string,
    tenantId: string,
    data: CaseDetailsInput
  ): Promise<CaseDetail> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/case-details/${caseId}?tenant_id=${tenantId}`,
      { method: 'PUT', headers, body: JSON.stringify(data) }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Field configuration admin (Administrator or super admin)
  // ============================================================================

  private adminFieldsUrl(kind: FieldKind, tenantId: string, suffix = ''): string {
    return `${requireApiBaseUrl()}/api/admin/fields/${kind}${suffix}?tenant_id=${tenantId}`;
  }

  /** Every field in one list, active and archived, with usage counts. */
  async listAdminFields(kind: FieldKind, tenantId: string): Promise<AdminField[]> {
    const headers = await this.getHeaders();
    const response = await fetch(this.adminFieldsUrl(kind, tenantId), { headers });
    return this.handleResponse(response);
  }

  async createAdminField(
    kind: FieldKind,
    tenantId: string,
    data: FieldCreateInput
  ): Promise<AdminField> {
    const headers = await this.getHeaders();
    const response = await fetch(this.adminFieldsUrl(kind, tenantId), {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async updateAdminField(
    kind: FieldKind,
    tenantId: string,
    configId: string,
    data: FieldUpdateInput
  ): Promise<AdminField> {
    const headers = await this.getHeaders();
    const response = await fetch(this.adminFieldsUrl(kind, tenantId, `/${configId}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  /** `configIds` must be exactly the active fields; a stale list is refused with 409. */
  async reorderAdminFields(
    kind: FieldKind,
    tenantId: string,
    configIds: string[]
  ): Promise<AdminField[]> {
    const headers = await this.getHeaders();
    const response = await fetch(this.adminFieldsUrl(kind, tenantId, '/order'), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ config_ids: configIds }),
    });
    return this.handleResponse(response);
  }

  async setAdminFieldArchived(
    kind: FieldKind,
    tenantId: string,
    configId: string,
    archived: boolean
  ): Promise<AdminField> {
    const headers = await this.getHeaders();
    const action = archived ? 'archive' : 'restore';
    const response = await fetch(
      this.adminFieldsUrl(kind, tenantId, `/${configId}/${action}`),
      { method: 'POST', headers }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Defendants
  // ============================================================================

  /** DELETE returns 204 (no body); can't use handleResponse() which calls response.json() */
  async deleteDefendant(caseId: string, defendantId: string, tenantId: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/defendants/${defendantId}?tenant_id=${tenantId}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    if (!response.ok) {
      await this.handleError(response);
    }
  }

  // ============================================================================
  // Case Participants
  // ============================================================================

  /**
   * List all participants for a case
   * Returns participant records with user details (email, name)
   *
   * Note: Backend returns flat array, frontend expects wrapped response.
   * We wrap it here to match CaseParticipantsResponse interface.
   */
  async getCaseParticipants(caseId: string, tenantId: string): Promise<CaseParticipantsResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/participants?tenant_id=${tenantId}`,
      { headers }
    );
    // Backend returns List[CaseParticipantResponse] (flat array)
    // Frontend expects { case_id, participants, count } wrapper
    const participants = await this.handleResponse<CaseParticipant[]>(response);
    return {
      case_id: caseId,
      participants: participants,
      count: participants.length
    };
  }

  /**
   * Add a user as a participant to a case
   * Requires case ownership or admin privileges
   * User must belong to the same tenant
   */
  async addCaseParticipant(
    caseId: string,
    tenantId: string,
    userId: string
  ): Promise<AddParticipantResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/participants?tenant_id=${tenantId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId }),
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Remove a participant from a case
   * Requires case ownership or admin privileges
   */
  async removeCaseParticipant(
    caseId: string,
    tenantId: string,
    userId: string
  ): Promise<RemoveParticipantResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/participants/${userId}?tenant_id=${tenantId}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Transfer case ownership to another user
   * Only the current owner or admin can transfer ownership
   * New owner must belong to the same tenant
   */
  async transferCaseOwnership(
    caseId: string,
    tenantId: string,
    newOwnerId: string
  ): Promise<TransferOwnershipResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/transfer-ownership?tenant_id=${tenantId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ new_owner_id: newOwnerId }),
      }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Files
  // ============================================================================

  async resubmitFile(
    fileId: string,
    tenantId: string,
    opts?: { force?: boolean; restartPoint?: string }
  ): Promise<ResubmitFileResponse> {
    const headers = await this.getHeaders();
    const qs = new URLSearchParams({ tenant_id: tenantId });
    if (opts?.force) qs.set('force', 'true');
    // vf-4-3: 'scan' re-runs ONLY the non-fatal AI Insights Scan stage.
    if (opts?.restartPoint) qs.set('restart_point', opts.restartPoint);
    const response = await fetch(
      `${requireApiBaseUrl()}/api/files/${fileId}/resubmit?${qs.toString()}`,
      {
        method: 'POST',
        headers,
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Bulk-retry every retryable failed file in a case with ONE request.
   *
   * Replaces the legacy "Retry All" fan-out (N concurrent
   * `resubmitFile` calls from the browser) that exhausted AlloyDB
   * connection slots during the 2026-06-01 internal-dev incident. The
   * server computes the retryable set (same predicate as
   * `isFileRetryable`) and enqueues a single Cloud Task to the
   * service-account bulk handler, which owns the per-tenant staggering.
   * Returns 202 with the queued count.
   */
  async retryFailedForCase(
    caseId: string,
    tenantId: string,
    fileIds?: string[],
  ): Promise<RetryFailedForCaseResponse> {
    const headers = await this.getHeaders();
    const qs = new URLSearchParams({ tenant_id: tenantId });
    const body = fileIds?.length ? JSON.stringify({ file_ids: fileIds }) : undefined;
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/retry-failed?${qs.toString()}`,
      {
        method: 'POST',
        headers: body
          ? { ...headers, 'Content-Type': 'application/json' }
          : headers,
        body,
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Fetch the structured-data extraction for a single sub-document
   * (Story 2.4 / 2.5). Powers the per-sub-document detail view linked
   * from the case Documents page.
   */
  async getSubDocumentExtraction(
    fileId: string,
    subDocumentKey: string,
    tenantId: string,
  ): Promise<SubDocumentExtraction> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/files/${fileId}/sub-documents/${encodeURIComponent(subDocumentKey)}/extraction?tenant_id=${tenantId}`,
      { headers },
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Batches
  // ============================================================================

  async createBatch(data: BatchCreateRequest): Promise<BatchCreateResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/batch/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async getBatches(params: {
    tenantId?: string;
    caseId?: string;
    limit?: number;
    offset?: number;
  }): Promise<BatchResponse[]> {
    const headers = await this.getHeaders();
    const searchParams = new URLSearchParams();

    if (params.tenantId) searchParams.append('tenant_id', params.tenantId);
    if (params.caseId) searchParams.append('case_id', params.caseId);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    const response = await fetch(
      `${requireApiBaseUrl()}/api/batches?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async getBatchStatus(batchId: string, tenantId: string) {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/batch/${batchId}/status?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Upload
  // ============================================================================

  async uploadFile(
    file: File,
    tenantId: string,
    caseId: string,
    batchId: string,
    createdBy: string
  ) {
    const token = await getAuthClient().getIdToken();

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${requireApiBaseUrl()}/api/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
        'X-Case-ID': caseId,
        'X-Batch-ID': batchId,
        'X-Created-By': createdBy,
      },
      body: formData,
    });

    return this.handleResponse(response);
  }

  // ============================================================================
  // Transcripts
  // ============================================================================

  async getCaseTranscripts(
    caseId: string,
    tenantId: string,
    params?: { limit?: number; offset?: number; search?: string }
  ): Promise<CaseTranscriptsResponse> {
    const headers = await this.getHeaders();
    const searchParams = new URLSearchParams({ tenant_id: tenantId });

    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    if (params?.search) searchParams.append('q', params.search);

    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/transcripts?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Insights Coverage (AI Insights 2.0, vf-2-1)
  // ============================================================================

  /**
   * Fetch the insights coverage state for a case (vf-2-1).
   *
   * Coverage is derived at read on the backend from
   * `pipeline_status.scan_status` (the authority, vf-4-3); the response is
   * content-free (counts + a timestamp only). State is as-of this call:
   * there is deliberately no polling (AC #6).
   *
   * Mirrors `getCaseTranscripts` for auth/error handling (bearer token via
   * `getHeaders`, `tenant_id` query param, `handleResponse` error mapping).
   */
  async getCaseInsightsState(
    caseId: string,
    tenantId: string
  ): Promise<InsightsStateResponse> {
    const headers = await this.getHeaders();
    const searchParams = new URLSearchParams({ tenant_id: tenantId });

    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/insights-state?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async retryConfirmation(caseId: string, idempotencyKey: string): Promise<{ status: string; generation: number }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/cases/${caseId}/confirmation/retry`, {
      method: 'POST', headers, body: JSON.stringify({ idempotency_key: idempotencyKey }),
    });
    return this.handleResponse(response);
  }

  // ============================================================================
  // AI flags (AI Insights 2.0, vf-1-3 read API / vf-2-2 span citations)
  // ============================================================================

  /**
   * Fetch verified AI flags for a case (vf-1-3, FR-18/FR-21).
   *
   * Defaults to verified flags only (the backend applies
   * `status = 'verified'` when no `status` filter is sent). `quote_text`,
   * `span_start_ms`, and `speaker` are server-derived at read; retracted
   * flags and flags on privilege-held transcripts are never returned.
   *
   * Mirrors `getCaseTranscripts` for auth/error handling (bearer token via
   * `getHeaders`, `tenant_id` query param, `handleResponse` error mapping).
   */
  async getCaseAiFlags(
    caseId: string,
    tenantId: string,
    opts?: { status?: string; review_status?: string; category?: string; limit?: number; relevant?: boolean }
  ): Promise<CaseAiFlagsResponse> {
    const headers = await this.getHeaders();
    const searchParams = new URLSearchParams({ tenant_id: tenantId });

    if (opts?.status) searchParams.append('status', opts.status);
    if (opts?.review_status) searchParams.append('review_status', opts.review_status);
    if (opts?.category) searchParams.append('category', opts.category);
    // vfr-1-4: relevance gate (vfr-1-2 backend). true = relevant/restored only
    // (unified view, rail, heat-map); false = the hidden set (restore inspection);
    // omit = all non-hidden incl. pending.
    if (opts?.relevant !== undefined) searchParams.append('relevant', String(opts.relevant));
    // vf-2-5: the Dossier resolves citations against the FULL verified set; a
    // capped read would falsely drop citations past the cap as "retracted".
    if (opts?.limit) searchParams.append('limit', String(opts.limit));

    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/ai-flags?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * Fetch the catalog's category display metadata (AI Insights 2.0, vf-2-3).
   *
   * Static, tenant-agnostic catalog data (no `tenant_id` param): the 7 catalog
   * categories with `{key, emoji, label, order}` from the catalog display
   * metadata. Bearer auth only (authenticated user). Mirrors the simple GET
   * shape of `getCaseAiFlags` for auth/error handling.
   */
  async getInsightsCategories(): Promise<InsightsCategoriesResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/insights/categories`,
      { headers }
    );
    return this.handleResponse<InsightsCategoriesResponse>(response);
  }

  /**
   * Set an AI flag's review status OR its `restored` override (vf-2-3 FR19 /
   * vfr-1-4 FR-R5).
   *
   * A USER write: audit-logged with actor = the calling user. Tenant-scoped +
   * bearer auth. The backend accepts EXACTLY ONE of `review_status`
   * (Ignore/Restore-from-ignored) or `restored` (un-hide a relevance=false flag,
   * survives re-runs). Returns the updated flag partial.
   */
  async patchAiFlagReview(
    caseId: string,
    aiFlagId: string,
    tenantId: string,
    action: { review_status: 'pending' | 'ignored' } | { restored: boolean }
  ): Promise<AiFlagReviewResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/ai-flags/${aiFlagId}/review?tenant_id=${tenantId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(action),
      }
    );
    return this.handleResponse<AiFlagReviewResponse>(response);
  }

  /**
   * Trigger a whole-case Re-run (vf-2-4, FR-24/FR-25). This HTTP call itself
   * is fire-and-forget: the backend takes the atomic re-run claim, enqueues
   * per-file scan tasks + a terminal refresh, and returns immediately; it
   * does not wait for the Re-run to finish.
   *
   * Completion visibility is NOT fire-and-forget, though (D5, 2026-07-01
   * audit; supersedes the original FR-31 "no polling, discover on next page
   * load" design): `useReanalyzeCase`'s `onSuccess` invalidates the
   * insights-state query, and `useInsightsState` polls every 60 s while
   * `rerun_in_progress` is true, so the analysis header's "Updating…" state
   * clears live once the server-side Re-run actually completes, no reload
   * required. Do not reintroduce fire-and-forget completion handling on the
   * assumption this comment's old wording still holds.
   */
  async reanalyzeCase(
    caseId: string,
    tenantId: string,
    idempotencyKey?: string
  ): Promise<ReanalyzeReceipt> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/reanalyze?tenant_id=${tenantId}`,
      { method: 'POST', headers: { ...headers, ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) } }
    );
    // Preserve the existing already-running conflict while distinguishing a
    // stale policy decision that admitted no work.
    if (response.status === 409) {
      try {
        const errorData = await response.json() as ErrorResponse;
        if (typeof errorData.detail === 'object' && errorData.detail !== null
          && errorData.detail.code === 'no_update_needed') {
          throw new ReanalyzeNoUpdateNeededError(errorData.detail.message);
        }
      } catch (error) {
        if (error instanceof ReanalyzeNoUpdateNeededError) throw error;
        // An unstructured or unreadable 409 retains the established conflict
        // behavior rather than leaking response parsing details to the UI.
      }
      throw new ReanalyzeConflictError();
    }
    return this.handleResponse(response);
  }

  /**
   * Fetch the case orientation (vf-2-5). With `includeDossier`, the response
   * carries the generated Dossier (case_orientation.dossier). The endpoint
   * returns 404 when NEITHER a brief nor a dossier exists yet (the
   * pre-generation state), which we map to `null` so the caller renders the
   * empty state instead of an error. (The endpoint audits a CJI read; expected.)
   */
  async getCaseOrientation(
    caseId: string,
    tenantId: string,
    opts?: { includeDossier?: boolean; includeCaseSummary?: boolean }
  ): Promise<CaseOrientationResponse | null> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (opts?.includeDossier) params.set('include_dossier', 'true');
    if (opts?.includeCaseSummary) params.set('include_case_summary', 'true');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/orientation?${params.toString()}`,
      { headers }
    );
    if (response.status === 404) return null; // no orientation yet
    return this.handleResponse<CaseOrientationResponse>(response);
  }

  /** Retry only the Overview Case Summary for the failed attempt the user saw. */
  async retryCaseSummary(
    caseId: string,
    tenantId: string,
    failedAttemptId: string,
    idempotencyKey: string,
  ): Promise<CaseSummaryRetryResponse> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams({ tenant_id: tenantId });
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/case-summary/retry?${params.toString()}`,
      {
        method: 'POST',
        headers: { ...headers, 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ failed_attempt_id: failedAttemptId }),
      },
    );
    return this.handleResponse<CaseSummaryRetryResponse>(response);
  }

  async reassignFileDefendant(
    fileId: string,
    tenantId: string,
    defendantId: string | null
  ): Promise<{ file_id: string; defendant_id: string | null }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/files/${fileId}?tenant_id=${tenantId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ defendant_id: defendantId }),
      }
    );
    return this.handleResponse(response);
  }

  async getTranscript(transcriptId: string, tenantId: string): Promise<TranscriptDetail> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async getTranscriptAudio(
    transcriptId: string,
    tenantId: string,
    // Optional `signal` added post-edv-4.8 code review (M3) so the
    // edv-4.8 `AudioPlayerBarSlot` can abort the in-flight metadata
    // probe on unmount or `(callId, tenantId)` change. Default
    // undefined preserves the existing call sites (legacy transcripts
    // page at `app/cases/[caseId]/transcripts/[transcriptId]/page.tsx:300`
    // and any future caller). Non-breaking additive parameter.
    signal?: AbortSignal,
  ): Promise<TranscriptAudioResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/audio?tenant_id=${tenantId}`,
      { headers, signal }
    );
    return this.handleResponse(response);
  }

  async getTranscriptSummary(transcriptId: string, tenantId: string): Promise<TranscriptSummary> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/summary?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse<TranscriptSummary>(response);
  }

  /**
   * Get batch summarization status for a transcript
   * Tracks Vertex AI batch prediction job progress
   *
   * @param transcriptId - Transcript UUID
   * @param _tenantId - Tenant UUID (not used, kept for API consistency)
   * @returns Batch job status with progress statistics
   * @throws Error if request fails or returns non-200 status
   */
  async getBatchSummarizationStatus(
    transcriptId: string,
    _tenantId: string
  ): Promise<BatchSummarizationStatus> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/batch-status/${transcriptId}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Batch job not found for this transcript');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(redactUuids(errorData.detail || 'Failed to fetch batch status'));
    }

    return response.json();
  }

  async updateTranscriptNotes(
    transcriptId: string,
    tenantId: string,
    notes: string
  ): Promise<TranscriptNotesUpdateResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/notes?tenant_id=${tenantId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ notes }),
      }
    );
    return this.handleResponse<TranscriptNotesUpdateResponse>(response);
  }

  async updateTranscriptUtterance(
    transcriptId: string,
    segmentIndex: number,
    text: string,
    tenantId: string
  ): Promise<UpdateUtteranceResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/utterances/${segmentIndex}?tenant_id=${tenantId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ text }),
      }
    );
    return this.handleResponse<UpdateUtteranceResponse>(response);
  }

  async getTranscriptUtteranceEdits(
    transcriptId: string,
    segmentIndex: number,
    tenantId: string
  ): Promise<UtteranceEdit[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/utterances/${segmentIndex}/edits?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse<UtteranceEdit[]>(response);
  }

  async rollbackTranscriptUtterance(
    transcriptId: string,
    segmentIndex: number,
    text: string,
    tenantId: string
  ): Promise<UpdateUtteranceResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/utterances/${segmentIndex}/rollback?tenant_id=${tenantId}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ text }),
      }
    );
    return this.handleResponse<UpdateUtteranceResponse>(response);
  }

  async getTranscriptNotes(transcriptId: string, tenantId: string): Promise<TranscriptNote[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/notes?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse<TranscriptNote[]>(response);
  }

  /** Every speaker in a transcript's lines with its naming status (gh #2017). */
  async getTranscriptSpeakers(transcriptId: string, tenantId: string): Promise<TranscriptSpeakersResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/speakers?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse<TranscriptSpeakersResponse>(response);
  }

  async createTranscriptNote(transcriptId: string, tenantId: string, noteText: string): Promise<TranscriptNote> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/notes?tenant_id=${tenantId}`,
      { method: 'POST', headers, body: JSON.stringify({ note_text: noteText }) }
    );
    return this.handleResponse<TranscriptNote>(response);
  }

  async updateTranscriptNote(transcriptId: string, noteId: string, tenantId: string, noteText: string): Promise<TranscriptNote> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/notes/${noteId}?tenant_id=${tenantId}`,
      { method: 'PATCH', headers, body: JSON.stringify({ note_text: noteText }) }
    );
    return this.handleResponse<TranscriptNote>(response);
  }

  async deleteTranscriptNote(transcriptId: string, noteId: string, tenantId: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/notes/${noteId}?tenant_id=${tenantId}`,
      { method: 'DELETE', headers }
    );
    if (!response.ok) {
      await this.handleError(response);
    }
  }

  async updateTranscriptStatus(
    transcriptId: string,
    tenantId: string,
    status: string
  ): Promise<TranscriptStatusUpdateResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/transcripts/${transcriptId}/status?tenant_id=${tenantId}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ transcript_status: status }),
      }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Admin
  // ============================================================================

  // PBX ingest settings for the ACTIVE tenant. Admin-only server-side; the
  // tenant is an explicit query param rather than inferred, matching every
  // other tenant-scoped admin call here.
  async getPbxSettings(tenantId: string): Promise<PbxSettings> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/pbx-settings?tenant_id=${encodeURIComponent(tenantId)}`,
      { headers },
    );
    return this.handleResponse(response);
  }

  // Omitted fields are left unchanged server-side, so a caller editing only the
  // participant list cannot accidentally reset the pairing window.
  async updatePbxSettings(
    tenantId: string,
    body: { pairing_window_hours?: number; default_participants?: string[] },
  ): Promise<PbxSettings> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/pbx-settings?tenant_id=${encodeURIComponent(tenantId)}`,
      { method: 'PUT', headers, body: JSON.stringify(body) },
    );
    return this.handleResponse(response);
  }

  // `tenantId` is the organisation the admin currently has selected. The
  // backend authorizes and scopes against it (gh #1700); without it the
  // request answers from the tenant the user logged into, which 403s an admin
  // of a second tenant and would serve the wrong users to a two-tenant admin.
  async getUsers(tenantId?: string): Promise<UserListResponse> {
    const headers = await this.getHeaders();
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const response = await fetch(`${requireApiBaseUrl()}/api/admin/users${qs}`, { headers });
    return this.handleResponse(response);
  }

  // Super-admin only: list every tenant in the system. Backend returns 403
  // for non-super_admin callers.
  async listAllTenants(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    status?: 'all' | 'active' | 'suspended' | 'inactive';
    sort?: 'name' | 'created_at' | 'user_count' | 'case_count';
    order?: 'asc' | 'desc';
  }): Promise<TenantListResponse> {
    const headers = await this.getHeaders();
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.append('limit', String(params.limit));
    if (params?.offset !== undefined) qs.append('offset', String(params.offset));
    if (params?.search) qs.append('search', params.search);
    if (params?.status) qs.append('status', params.status);
    if (params?.sort) qs.append('sort', params.sort);
    if (params?.order) qs.append('order', params.order);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await fetch(`${requireApiBaseUrl()}/api/admin/tenants${suffix}`, { headers });
    return this.handleResponse(response);
  }

  // Super-admin only: grant an existing user a role in a target tenant.
  // Mirrors the upload-web super-admin "Add Existing User" flow.
  async addUserToTenant(
    tenantId: string,
    userId: string,
    role: UserRole,
  ): Promise<AddUserToTenantResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/tenants/${tenantId}/users`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId, role }),
      },
    );
    return this.handleResponse(response);
  }

  async getEligibleParticipants(caseId: string, tenantId: string): Promise<EligibleParticipantsResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/eligible-participants?tenant_id=${tenantId}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async updateUserProfile(
    userId: string,
    data: { first_name?: string; last_name?: string; phone_number?: string },
    tenantId?: string,
  ) {
    const headers = await this.getHeaders();
    // See updateUserRole for why tenantId goes on the query string.
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/users/${userId}/profile${qs}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse(response);
  }

  /**
   * Update current user's own profile (self-service).
   * Different from updateUserProfile which is admin-only.
   */
  async updateMyProfile(data: {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
  }): Promise<{ success: boolean; updated_fields: string[] }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/auth/me`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse(response);
  }

  async updateUserRole(userId: string, role: string, tenantId?: string) {
    const headers = await this.getHeaders();
    // tenantId (super-admin only) goes on the query string, NOT in the
    // body: the backend exposes it as FastAPI Query(...) on
    // PATCH /api/admin/users/{id}/role. Body-only submissions silently
    // drop the field and the endpoint falls back to auth.tenant_id,
    // which produces "User role not found or already inactive" when the
    // target user lives on a different tenant than the caller.
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const response = await fetch(
      `/api/admin/users/${userId}/role${qs}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ role }),
      }
    );
    return this.handleResponse(response);
  }

  async updateUserStatus(userId: string, active: boolean, tenantId?: string) {
    const headers = await this.getHeaders();
    // tenantId only scopes the audit-log attribution on this endpoint
    // (status is a global "user" row flag). Still put it on the query
    // string to match the backend Query(...) contract; missing → audit
    // attributes to the super-admin's active tenant.
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/users/${userId}/status${qs}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ active }),
      }
    );
    return this.handleResponse(response);
  }

  async revokeUserAccess(userId: string, reason?: string, targetTenantId?: string) {
    const headers = await this.getHeaders();
    // targetTenantId (super-admin only) picks which tenant's grant to
    // revoke. Omitted → backend uses the admin's currently-active tenant.
    const qs = targetTenantId ? `?tenant_id=${encodeURIComponent(targetTenantId)}` : '';
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/users/${userId}/tenant-access${qs}`,
      {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ reason }),
      }
    );
    return this.handleResponse(response);
  }

  // tenantId is REQUIRED: the backend otherwise falls back to the tenant the
  // admin authenticated against, not the one they are acting in (issue #1270).
  async inviteUser(email: string, role: string, tenantId: string) {
    // Use local API route to handle authentication via cookie
    const response = await fetch('/api/admin/users/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, role, tenant_id: tenantId }),
    });
    return this.handleResponse(response);
  }

  async listInvitations(tenantId: string): Promise<InvitationListResponse> {
    // Use local API route to handle authentication via cookie
    const response = await fetch(
      `/api/admin/invitations?tenant_id=${tenantId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return this.handleResponse(response);
  }

  async resendInvitation(tenantId: string, userId: string): Promise<ResendInvitationResponse> {
    // Use local API route to handle authentication via cookie
    const response = await fetch(
      `/api/admin/invitations/${userId}/resend?tenant_id=${tenantId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return this.handleResponse(response);
  }

  async cancelInvitation(tenantId: string, userId: string): Promise<CancelInvitationResponse> {
    // Use local API route to handle authentication via cookie
    const response = await fetch(
      `/api/admin/invitations/${userId}?tenant_id=${tenantId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // CONVERSATIONS (Added 2025-11-07)
  // ============================================================================
  // NOTE: live chat streaming does NOT go through this client: the webapp's
  // /api/chat route (AI SDK, Fix 137) owns it. The chat-proxy-era SSE helpers
  // (streamChat, streamConversationChat) were removed 2026-07-01.

  /**
   * Create new conversation with Vertex AI session
   * Backend creates Vertex AI session and returns session ID
   * Uses local API route to avoid CORS issues
   * Includes 30-second timeout to prevent indefinite hanging
   */
  async createConversation(
    caseId: string,
    tenantId: string,
    title?: string
  ): Promise<Conversation> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const headers = await this.getHeaders();
      const response = await fetch(
        `${requireApiBaseUrl()}/api/conversations?tenant_id=${encodeURIComponent(tenantId)}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ case_id: caseId, title }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);
      return this.handleResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - conversation creation took too long. Please try again.');
      }
      throw error;
    }
  }

  async listConversations(params: {
    tenantId: string;
    caseId?: string;
    userId?: string;
    includeArchived?: boolean;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  }): Promise<ConversationListItem[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('tenant_id', params.tenantId);

    if (params.caseId) searchParams.append('case_id', params.caseId);
    if (params.userId) searchParams.append('user_id', params.userId);
    if (params.includeArchived) searchParams.append('include_archived', 'true');
    if (params.searchQuery) searchParams.append('search_query', params.searchQuery);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async getConversation(
    conversationId: string,
    tenantId: string
  ): Promise<Conversation> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}?tenant_id=${encodeURIComponent(tenantId)}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  async getConversationMessages(
    conversationId: string,
    tenantId: string,
    params?: { limit?: number; beforeMessageId?: string }
  ): Promise<ConversationMessage[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('tenant_id', tenantId);

    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.beforeMessageId) searchParams.append('before_message_id', params.beforeMessageId);

    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}/messages?${searchParams.toString()}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * #863: Deterministically finalize a conversation's active streaming
   * assistant message after a user-initiated Stop. Calls upload-api's
   * `/stop-streaming`, which flips any status='streaming' row to a terminal
   * status='complete' (preserving any partial text persisted by the chat
   * route's onAbort, else a placeholder). Idempotent: returns
   * `stopped_count: 0` when there is no streaming row left to finalize.
   */
  async stopConversationStreaming(
    conversationId: string,
    tenantId: string
  ): Promise<{ conversation_id: string; stopped_count: number; stopped_message_ids: string[] }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}/stop-streaming?tenant_id=${encodeURIComponent(tenantId)}`,
      {
        method: 'POST',
        headers,
      }
    );
    return this.handleResponse(response);
  }

  async updateConversation(
    conversationId: string,
    tenantId: string,
    data: UpdateConversationRequest
  ): Promise<Conversation> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}?tenant_id=${encodeURIComponent(tenantId)}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse(response);
  }

  async deleteConversation(
    conversationId: string,
    tenantId: string
  ): Promise<DeleteConversationResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}?tenant_id=${encodeURIComponent(tenantId)}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    return this.handleResponse(response);
  }

  async exportConversation(
    conversationId: string,
    tenantId: string
  ): Promise<ConversationExport> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}/export?tenant_id=${encodeURIComponent(tenantId)}`,
      { headers }
    );
    return this.handleResponse(response);
  }

  /**
   * Conversation rendered as a PDF by upload-api. Returns the bytes; naming and
   * saving the file is left to the caller, as with the JSON export.
   */
  async exportConversationPdf(
    conversationId: string,
    tenantId: string
  ): Promise<Blob> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/conversations/${conversationId}/export/pdf?tenant_id=${encodeURIComponent(tenantId)}`,
      { headers }
    );
    if (!response.ok) {
      await this.handleError(response);
    }
    return response.blob();
  }

  /**
   * Timeline export page estimate and unconfirmed-speakers notice (gh #2019).
   * Not audited server-side.
   */
  async estimateTimelineExport(
    caseId: string,
    tenantId: string,
    body: TimelineExportRequest,
    signal?: AbortSignal,
  ): Promise<TimelineExportEstimate> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/timeline-export?tenant_id=${encodeURIComponent(tenantId)}`,
      { method: 'POST', headers, body: JSON.stringify({ ...body, purpose: 'estimate' }), signal }
    );
    return this.handleResponse<TimelineExportEstimate>(response);
  }

  /**
   * Timeline export PDF (gh #2019). `purpose` is 'download' or 'print'; each
   * writes one audit row. Naming and saving the file is left to the caller,
   * which gets the server's Content-Disposition and page count.
   */
  async downloadTimelineExport(
    caseId: string,
    tenantId: string,
    body: TimelineExportRequest,
    purpose: 'download' | 'print' = 'download',
    signal?: AbortSignal,
  ): Promise<TimelineExportFile> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/timeline-export?tenant_id=${encodeURIComponent(tenantId)}`,
      { method: 'POST', headers, body: JSON.stringify({ ...body, purpose }), signal }
    );
    if (!response.ok) {
      await this.handleError(response);
    }
    const pageCount = Number(response.headers.get('X-Page-Count'));
    return {
      blob: await response.blob(),
      pageCount: Number.isFinite(pageCount) && pageCount > 0 ? pageCount : null,
      contentDisposition: response.headers.get('Content-Disposition'),
    };
  }

  // ============================================================================
  // Entity Knowledge Graph
  // ============================================================================


  async getEntity(
    entityId: string,
    tenantId: string,
    caseId: string,
  ): Promise<EntityDetail> {
    if (!tenantId) throw new Error('tenantId is required');
    if (!caseId) throw new Error('caseId is required');
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    // Issue #439: case-scoped detail panel. The route REQUIRES
    // case_id server-side so case-scoped provenance is enforced at the
    // wire (the cross-case tenant-wide admin reader was removed with the
    // Entities explorer, 2026-06-09).
    const params = new URLSearchParams({
      tenant_id: tenantId,
      case_id: caseId,
    });
    const response = await fetch(
      `${requireApiBaseUrl()}/api/entities/${entityId}?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return this.handleResponse<EntityDetail>(response);
  }

  async rebuildTenantSpannerGraph(tenantId: string): Promise<RebuildTenantGraphResponse> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/tenants/${tenantId}/rebuild-graph`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return this.handleResponse(response);
  }

  async getPendingSpannerSyncCount(): Promise<SpannerSyncPendingResponse> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/admin/spanner-sync/pending`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return this.handleResponse(response);
  }

  async getCaseEntityGraph(
    caseId: string,
    tenantId: string
  ): Promise<{ case_id: string; nodes: EntityResponse[]; edges: EntityRelationshipResponse[] }> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/entity-graph?tenant_id=${tenantId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return this.handleResponse(response);
  }

  // Knowledge Graph Timeline tab (PRD prd-knowledge-graph-timeline.md
  // §7.1). Server-side three-tier sort: parseable-ISO first (asc by
  // datetime_iso), then approximate-raw (asc by datetime_raw), then
  // undated (asc by created_at). Client-side filtering / search /
  // bucketing operates on this already-sorted list; see
  // useTimelineFilters / useTimelineSearch.
  async getCaseTimelineEvents(
    caseId: string,
    tenantId: string,
  ): Promise<TimelineEventsResponse> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${caseId}/timeline-events?tenant_id=${tenantId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return this.handleResponse(response);
  }

  /**
   * The case's grouped voices for the Voices view. The whole list comes at
   * once; search, filters, sorting and paging run in the browser.
   */
  async getCaseVoices(caseId: string, tenantId: string): Promise<CaseVoicesResponse> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${encodeURIComponent(caseId)}/voices?tenant_id=${encodeURIComponent(tenantId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return this.handleResponse(response);
  }

  /**
   * One voice for the voice drawer: its head, suggestion, samples, and one
   * page of its calls. `limit` defaults to 50 on the server and is capped at
   * 200 there.
   */
  async getCaseVoice(
    caseId: string,
    voiceId: string,
    tenantId: string,
    page: CaseVoiceCallsPage = {},
  ): Promise<CaseVoiceDetailResponse> {
    const token = await this.getToken();
    if (!token) throw new Error('Not authenticated');
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (page.offset !== undefined) params.set('offset', String(page.offset));
    if (page.limit !== undefined) params.set('limit', String(page.limit));
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${encodeURIComponent(caseId)}/voices/${encodeURIComponent(voiceId)}?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return this.handleResponse(response);
  }

  /**
   * Name a voice, change its person's name, or merge it into a person (naming,
   * version 1). Answers the row's first detail page as it is after the save;
   * a 409 means the drawer was out of date or voice grouping was running.
   */
  async nameCaseVoice(
    caseId: string,
    voiceId: string,
    tenantId: string,
    body: CaseVoiceNameRequest,
  ): Promise<CaseVoiceDetailResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/cases/${encodeURIComponent(caseId)}/voices/${encodeURIComponent(voiceId)}/name?tenant_id=${encodeURIComponent(tenantId)}`,
      { method: 'PUT', headers, body: JSON.stringify(body) }
    );
    return this.handleResponse(response);
  }

  async getFeatureFlags(tenantId: string): Promise<Record<string, boolean>> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${requireApiBaseUrl()}/api/feature-flags/evaluated?tenant_id=${tenantId}`,
      { headers },
    );
    return this.handleResponse(response);
  }

  // Feature Flag Admin (super_admin)

  async listFeatureFlags(): Promise<FeatureFlagAdmin[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/feature-flags`, { headers });
    return this.handleResponse(response);
  }

  async createFeatureFlag(flag: { flag_key: string; description?: string; enabled?: boolean }): Promise<{ flag_key: string; enabled: boolean }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/feature-flags`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(flag),
    });
    return this.handleResponse(response);
  }

  async updateFeatureFlag(flagKey: string, update: { enabled?: boolean; description?: string }): Promise<unknown> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/feature-flags/${flagKey}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    });
    return this.handleResponse(response);
  }

  async setTenantFlagOverride(flagKey: string, tenantId: string, enabled: boolean): Promise<unknown> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/feature-flags/${flagKey}/tenants/${tenantId}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    return this.handleResponse(response);
  }

  async removeTenantFlagOverride(flagKey: string, tenantId: string): Promise<unknown> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/feature-flags/${flagKey}/tenants/${tenantId}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse(response);
  }

  async sendSupportRequest(payload: SupportContactRequest): Promise<SupportContactResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${requireApiBaseUrl()}/api/support/contact`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return this.handleResponse(response);
  }
}

export type SupportCategory = 'bug' | 'question' | 'feature' | 'other';

export interface SupportContactRequest {
  tenant_id: string;
  category: SupportCategory;
  subject: string;
  message: string;
}

export interface SupportContactResponse {
  success: boolean;
  message_id: string;
}

export interface EntityResponse {
  entity_id: string;
  canonical_name: string;
  entity_type: string;
  properties: Record<string, unknown>;
  merged_into: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Timeline tab wire shapes (mirrors services/upload-api/main.py::
// TimelineEvent / TimelineEventsResponse). Parsed datetimes and sort
// order are server-side responsibilities: the client never re-sorts
// across buckets, only filters within.
export interface TimelinePrimarySource {
  source_type: string;
  source_id: string;
  display_name: string;
}

export interface TimelineConnectedEntity {
  entity_id: string;
  name: string;
  entity_type: string;
}

export interface TimelineEvent {
  entity_id: string;
  name: string;
  entity_type: string;
  // `datetime_raw` is the verbatim string from the extractor (FR-C2 /
  // FR-T18: rendered as-is on the card). `null` only when the event
  // has no datetime field at all (Undated bucket).
  datetime_raw: string | null;
  // `datetime_iso` is the parsed ISO-8601 form, or `null` when the
  // raw value doesn't parse (Approximate bucket). Used for date-range
  // filter comparisons + month-year group bucketing.
  datetime_iso: string | null;
  source_count: number;
  // `null` when the entity has no mention in this case (defense-in-
  // depth; that shouldn't normally happen since the reader joins on
  // entity_mention).
  primary_source: TimelinePrimarySource | null;
  // Flat list, no cap (PRD D5: show everything). Each entry is
  // clickable in the card and opens the entity panel on the
  // connected entity.
  connected_entities: TimelineConnectedEntity[];
  // ISO-8601 timestamp. On AlloyDB this is entity.created_at; on
  // Spanner it falls back to entity.updated_at (no created_at column).
  // Used only for the undated-bucket stable sort.
  created_at: string;
}

export interface TimelineEventsResponse {
  case_id: string;
  events: TimelineEvent[];
}

export interface EntityMention {
  mention_id: string;
  case_id: string;
  source_type: string;
  source_id: string;
  surface_form: string;
  context_snippet: string | null;
  confidence: number | null;
  resolution_status: string;
  resolved_by: string | null;
  created_at: string | null;
}

export interface EntityRelationshipResponse {
  relationship_id: string;
  source_entity_id: string;
  target_entity_id: string;
  relationship_type: string;
  properties: Record<string, unknown>;
  source_case_id: string | null;
  // Composite edge confidence (migration 121 + 130). Derived from
  // mean(properties.llm_confidences), support_count, source_breadth, and the
  // relationship_type prior. The raw LLM-self-reported probability is no
  // longer the primary signal; this value has real variance across [0, 1].
  confidence: number | null;
  // Number of distinct entity_mention rows backing this edge. Input to the
  // composite formula; surface in the UI as "supported by N mentions".
  // Optional/nullable: legacy rows pre-mig-121 and edges read from a
  // not-yet-resynced Spanner projection may omit it.
  support_count?: number | null;
  // Number of distinct (source_type, source_id) tuples among the backing
  // mentions: the count of independent extractions corroborating the
  // edge. Surface as "across M sources". Optional for the same reasons as
  // support_count.
  source_breadth?: number | null;
  connected_name?: string;
  connected_type?: string;
}

export interface EntityDetail extends EntityResponse {
  tenant_id: string;
  mentions: EntityMention[];
  relationships: EntityRelationshipResponse[];
}

export interface FeatureFlagTenantOverride {
  tenant_id: string;
  enabled: boolean;
}

export interface FeatureFlagAdmin {
  flag_key: string;
  description: string | null;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  tenant_overrides: FeatureFlagTenantOverride[];
}

export interface RebuildTenantGraphCaseResult {
  case_id: string;
  status: string;
  entities_to_resync: number;
  chunks_to_resync: number;
  tasks_enqueued: number;
}

export interface RebuildTenantGraphCaseFailure {
  case_id: string;
  status_code: number;
  detail: unknown;
}

export interface RebuildTenantGraphResponse {
  status: 'ok' | 'partial' | 'failed';
  tenant_id: string;
  case_count: number;
  succeeded: number;
  failed: number;
  entities_to_resync: number;
  chunks_to_resync: number;
  tasks_enqueued: number;
  results: RebuildTenantGraphCaseResult[];
  failures: RebuildTenantGraphCaseFailure[];
}

export interface SpannerSyncPendingResponse {
  pending: number;
  queue: string | null;
  available: boolean;
}

export const apiClient = new ApiClient();

registerAuthResetHandlers({
  clearApiTokenCache: () => apiClient.clearTokenCache(),
});
